/**
 * Visa Checklist Component
 * 
 * Displays a checklist of visa requirements and allows users to track completion.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { View, Pressable, ScrollView, Linking, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import type { VisaRequirement, VisaChecklistItem } from '@/services/visaChecker';
import { generateVisaChecklist, getVisaStatusMessage, linkVisaDocuments } from '@/services/visaChecker';
import type { Trip } from '@/services/trips';
import type { Document } from '@/services/documentVault';
import { loadVisaChecklistCompletions, saveVisaChecklistCompletions } from '@/services/visaChecklistStorage';
import { syncVisaChecklistTripNotification } from '@/notifications/service';

type VisaChecklistProps = {
  requirement: VisaRequirement;
  trip: Trip;
  documents?: Document[];
  onDocumentLink?: (documentId: string) => void;
};

export function VisaChecklist({
  requirement,
  trip,
  documents = [],
  onDocumentLink,
}: VisaChecklistProps) {
  const theme = useTheme();
  const [userCompletion, setUserCompletion] = useState<Record<string, boolean>>({});
  const hadAllItemsCompleteRef = useRef(false);
  const baseChecklistRef = useRef<VisaChecklistItem[]>([]);

  const baseChecklist = useMemo(() => {
    let items = generateVisaChecklist(requirement, trip);
    items = linkVisaDocuments(items, documents);
    return items;
  }, [
    requirement.status,
    requirement.destination,
    requirement.passportCountry,
    requirement.lastUpdated,
    trip.id,
    trip.startDate?.getTime?.(),
    trip.endDate?.getTime?.(),
    documents
      .map(
        (doc) =>
          `${doc.id}:${doc.type}:${doc.updatedAt ? new Date(doc.updatedAt).getTime() : 0}`,
      )
      .join('|'),
  ]);

  useEffect(() => {
    baseChecklistRef.current = baseChecklist;
  }, [baseChecklist]);

  const checklist: VisaChecklistItem[] = useMemo(() => {
    return baseChecklist.map((item) => ({
      ...item,
      completed:
        userCompletion[item.id] !== undefined ? userCompletion[item.id]! : item.completed,
    }));
  }, [baseChecklist, userCompletion]);

  useEffect(() => {
    setUserCompletion({});
    let cancelled = false;
    loadVisaChecklistCompletions(trip.id).then((loaded) => {
      if (cancelled) return;
      setUserCompletion(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [trip.id]);

  const normalizeUrl = (raw: string): string => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
    // If it's a bare domain, best-effort add https.
    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(trimmed)) return `https://${trimmed}`;
    return trimmed;
  };

  const toggleItem = (itemId: string) => {
    setUserCompletion((prev) => {
      const base = baseChecklistRef.current.find((i) => i.id === itemId);
      const current =
        prev[itemId] !== undefined
          ? prev[itemId]!
          : (base?.completed ?? false);
      const nextCompleted = !current;
      const next = { ...prev, [itemId]: nextCompleted };
      void saveVisaChecklistCompletions(trip.id, next);
      return next;
    });
  };

  const handleOpenLink = async (url: string) => {
    try {
      const normalized = normalizeUrl(url);
      const supported = await Linking.canOpenURL(normalized);
      if (supported) {
        await Linking.openURL(normalized);
      } else {
        Alert.alert('Error', 'Cannot open this link.');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to open link.');
    }
  };

  const renderLinkifiedText = (
    text: string,
    opts?: { variant?: 'body' | 'caption'; textColor?: string; linkColor?: string },
  ) => {
    const variant = opts?.variant ?? 'body';
    const textColor = opts?.textColor;
    const linkColor = opts?.linkColor ?? theme.colors.primary;

    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const parts = text.split(urlRegex);

    return (
      <ThemedText variant={variant} color={textColor}>
        {parts.map((part, idx) => {
          if (!part) return null;
          // Reset regex state due to global flag before each test.
          urlRegex.lastIndex = 0;
          const isUrl = urlRegex.test(part);
          if (!isUrl) return part;
          return (
            <ThemedText
              key={`link-${idx}-${part}`}
              variant={variant}
              color={linkColor}
              onPress={() => handleOpenLink(part)}
              style={{ textDecorationLine: 'underline' }}
            >
              {part}
            </ThemedText>
          );
        })}
      </ThemedText>
    );
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const completedCount = checklist.filter((item) => item.completed).length;
  const totalCount = checklist.length;
  const progress = totalCount > 0 ? completedCount / totalCount : 0;
  const checklistFullyComplete = totalCount > 0 && completedCount === totalCount;

  useEffect(() => {
    if (!trip?.id || totalCount === 0) return;
    const allComplete = completedCount === totalCount;
    if (allComplete) {
      syncVisaChecklistTripNotification({
        tripId: trip.id,
        tripName: trip.name,
        allItemsComplete: true,
        shouldRevertRead: false,
      });
      hadAllItemsCompleteRef.current = true;
    } else if (hadAllItemsCompleteRef.current) {
      syncVisaChecklistTripNotification({
        tripId: trip.id,
        tripName: trip.name,
        allItemsComplete: false,
        shouldRevertRead: true,
      });
      hadAllItemsCompleteRef.current = false;
    }
  }, [trip?.id, trip?.name, completedCount, totalCount]);

  const statusToneColor = checklistFullyComplete
    ? (theme.colors.success || '#34C759')
    : requirement.visaRequired
      ? theme.colors.error
      : (theme.colors.success || '#34C759');
  const statusIconName = checklistFullyComplete
    ? 'checkmark-circle'
    : requirement.visaRequired
      ? 'alert-circle'
      : 'checkmark-circle';
  const statusLabel = checklistFullyComplete
    ? 'All visa steps completed'
    : requirement.visaRequired
      ? 'Visa required'
      : 'Visa not required';

  return (
    <Card variant="outlined">
      <View style={{ gap: theme.spacing(2) }}>
        <View>
          <SubHeading>Visa Requirements Checklist</SubHeading>
          <View
            style={{
              marginTop: theme.spacing(1),
              paddingVertical: theme.spacing(1),
              paddingHorizontal: theme.spacing(1.25),
              borderRadius: theme.radii.md,
              borderWidth: 1,
              borderColor: statusToneColor,
              backgroundColor: statusToneColor + '12',
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(0.75),
            }}
          >
            <Ionicons
              name={statusIconName}
              size={18}
              color={statusToneColor}
            />
            <ThemedText
              variant="bodyStrong"
              style={{
                color: statusToneColor,
              }}
            >
              {statusLabel}
            </ThemedText>
          </View>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            {getVisaStatusMessage(requirement)}
          </Body>
        </View>

        {requirement.notes && (
          <View
            style={{
              padding: theme.spacing(1.5),
              backgroundColor: theme.colors.background,
              borderRadius: theme.radii.md,
            }}
          >
            {renderLinkifiedText(requirement.notes, { variant: 'body' })}
          </View>
        )}

        {totalCount > 0 && (
          <View>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <Caption color={theme.colors.textSecondary}>
                {completedCount} of {totalCount} completed
              </Caption>
              <Caption color={theme.colors.textSecondary}>
                {Math.round(progress * 100)}%
              </Caption>
            </View>
            <View
              style={{
                height: 4,
                backgroundColor: theme.colors.borderMuted,
                borderRadius: theme.radii.sm,
                overflow: 'hidden',
              }}
            >
              <View
                style={{
                  height: '100%',
                  width: `${progress * 100}%`,
                  backgroundColor: theme.colors.primary,
                }}
              />
            </View>
          </View>
        )}

        {totalCount === 0 ? (
          <View
            style={{
              paddingVertical: theme.spacing(3),
              alignItems: 'center',
              gap: theme.spacing(1),
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              borderRadius: theme.radii.md,
              backgroundColor: theme.colors.background,
            }}
          >
            <Ionicons name="document-text-outline" size={24} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              No checklist items available yet.
            </Body>
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              We could not generate a visa checklist for this trip right now.
            </Caption>
          </View>
        ) : (
          <ScrollView style={{ maxHeight: 400 }}>
            <View style={{ gap: theme.spacing(1.5) }}>
              {checklist.map((item) => (
                <Pressable
                  key={item.id}
                  onPress={() => toggleItem(item.id)}
                  style={({ pressed }) => ({
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: theme.spacing(1.5),
                    padding: theme.spacing(1.5),
                    backgroundColor: item.completed
                      ? theme.colors.primary + '10'
                      : theme.colors.background,
                    borderRadius: theme.radii.md,
                    borderWidth: 1,
                    borderColor: item.completed
                      ? theme.colors.primary + '30'
                      : theme.colors.borderMuted,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: theme.radii.sm,
                      borderWidth: 2,
                      borderColor: item.completed
                        ? theme.colors.primary
                        : theme.colors.borderMuted,
                      backgroundColor: item.completed
                        ? theme.colors.primary
                        : 'transparent',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 2,
                    }}
                  >
                    {item.completed && (
                      <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
                    )}
                  </View>
                  <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                    <ThemedText
                      variant="body"
                      style={{
                        textDecorationLine: item.completed ? 'line-through' : 'none',
                        opacity: item.completed ? 0.6 : 1,
                      }}
                    >
                      {item.requirement}
                    </ThemedText>
                    {item.dueDate && (
                      <Caption color={theme.colors.textSecondary}>
                        Due: {formatDate(item.dueDate)}
                      </Caption>
                    )}
                    {(item.notes || (item.linkUrl && item.linkLabel)) && (
                      <View>
                        {item.linkUrl && item.linkLabel ? (
                          <ThemedText variant="caption" color={theme.colors.textSecondary}>
                            {item.notes}
                            <ThemedText
                              variant="caption"
                              color={theme.colors.primary}
                              onPress={() => handleOpenLink(item.linkUrl!)}
                              style={{ textDecorationLine: 'underline' }}
                            >
                              {item.linkLabel}
                            </ThemedText>
                          </ThemedText>
                        ) : (
                          renderLinkifiedText(item.notes!, {
                            variant: 'caption',
                            textColor: theme.colors.textSecondary,
                          })
                        )}
                      </View>
                    )}
                    {item.documentId && (
                      <Pressable
                        onPress={() => onDocumentLink?.(item.documentId!)}
                        style={({ pressed }) => ({
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: theme.spacing(0.5),
                          marginTop: theme.spacing(0.5),
                          opacity: pressed ? 0.7 : 1,
                        })}
                      >
                        <Ionicons
                          name="document-text-outline"
                          size={14}
                          color={theme.colors.primary}
                        />
                        <Caption color={theme.colors.primary} style={{ fontSize: 12 }}>
                          View linked document
                        </Caption>
                      </Pressable>
                    )}
                  </View>
                </Pressable>
              ))}
            </View>
          </ScrollView>
        )}

        {(requirement.eVisaLink || requirement.embassyLink) && (
          <View style={{ gap: theme.spacing(1) }}>
            {requirement.eVisaLink && (
              <Button
                label="Apply for E-Visa"
                variant="secondary"
                onPress={() => handleOpenLink(requirement.eVisaLink!)}
                icon={<Ionicons name="globe-outline" size={18} color={theme.colors.text} />}
              />
            )}
            {requirement.embassyLink && (
              <Button
                label="Find Embassy/Consulate"
                variant="secondary"
                onPress={() => handleOpenLink(requirement.embassyLink!)}
                icon={<Ionicons name="location-outline" size={18} color={theme.colors.text} />}
              />
            )}
          </View>
        )}
      </View>
    </Card>
  );
}

